import { toast } from "@carbon6/c6-frontend-design";
import {
  getTrainingSessionCompanionFileDownload,
  getTrainingSessionPolicyDownload,
  getTrainingSessionRawCheckpointDownload,
  type CompanionFileView,
  type RawCheckpointView,
} from "@/api";
import { isHttpClientError } from "@/http";
import { createLogger } from "@/logging";
import { triggerBrowserDownload } from "@/utils";

const logger = createLogger("page/training/checkpointDownload");

/**
 * 체크포인트 1회용 signed URL 을 받아 브라우저에 그대로 전달한다.
 *
 * registry 는 byte 를 중계하지 않는다. 체크포인트는 크기 상한이 없어 blob 으로 메모리에 올리지
 * 않는다(`triggerBrowserDownload` 의 crossOrigin 경로). 세션 상세의 결과 분석 카드와 체크포인트
 * 탭이 이 함수 하나를 함께 쓴다. 그래서 파일명 규칙과 실패 문구도 여기서만 정한다. 실패는 무시하지
 * 않고 토스트로 알리지만 예외를 다시 던지지는 않는다. 호출부는 pending 표시만 해제하면 된다.
 */
export async function downloadSessionCheckpoint(sessionId: string, epoch: string): Promise<void> {
  try {
    const { download_url } = await getTrainingSessionPolicyDownload(sessionId, epoch);
    triggerBrowserDownload(download_url, {
      filename: `checkpoint-epoch-${epoch}`,
      crossOrigin: true,
    });
  } catch (error) {
    logger.error("checkpoint download url failed", error);
    toast.error("Checkpoint 다운로드 링크를 가져오지 못했습니다. 잠시 후 다시 시도해 주세요.");
  }
}

// 파일 본문은 다른 다운로드와 같이 브라우저가 직접 받는다.
export async function downloadSessionRawCheckpoint(
  sessionId: string,
  file: RawCheckpointView,
): Promise<void> {
  try {
    const { download_url, filename } = await getTrainingSessionRawCheckpointDownload(
      sessionId,
      file,
    );
    triggerBrowserDownload(download_url, { filename, crossOrigin: true });
  } catch (error) {
    logger.error("raw checkpoint download url failed", error);
    toast.error(
      "Raw Checkpoint 다운로드 링크를 가져오지 못했습니다. 목록을 새로고침한 뒤 다시 시도해 주세요.",
    );
  }
}

// 서버는 부속 파일 목록에 있는 파일만 서명한다. 목록을 읽은 뒤 파일이 사라졌으면 404 다.
export async function downloadSessionCompanionFile(
  sessionId: string,
  file: CompanionFileView,
): Promise<void> {
  try {
    const { download_url, filename } = await getTrainingSessionCompanionFileDownload(
      sessionId,
      file,
    );
    triggerBrowserDownload(download_url, { filename, crossOrigin: true });
  } catch (error) {
    logger.error("companion file download url failed", error);
    toast.error(
      isHttpClientError(error) && error.code === "training.companion_file_not_found"
        ? "부속 파일을 찾을 수 없습니다. 목록을 새로고침한 뒤 다시 시도해 주세요."
        : "부속 파일 다운로드 링크를 가져오지 못했습니다. 잠시 후 다시 시도해 주세요.",
    );
  }
}
